import {
    mdiAlertCircle,
    mdiBankOutline,
    mdiCheck,
    mdiClose,
    mdiHistory,
    mdiMagnify,
    mdiSubdirectoryArrowRight,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { CatalogPayer } from '../../../services/worker-configuration/automation/worker-automation.types';
import { getUsageBadgePresentation } from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import * as S from './configuration-rule.styles';

interface IConfigurationRulePayerEditorBaseProps {
    disabled?: boolean;
    payerIds: string[];
    store: WorkerStore;
}

interface IConfigurationRulePayerEditorRuleProps extends IConfigurationRulePayerEditorBaseProps {
    matchIndex: number;
    onChange?: never;
    pickerKey?: never;
    ruleId: string;
    showMatchHint?: boolean;
}

interface IConfigurationRulePayerEditorListProps extends IConfigurationRulePayerEditorBaseProps {
    matchIndex?: never;
    onChange: (payerIds: string[]) => void;
    pickerKey: string;
    ruleId?: never;
    showMatchHint?: boolean;
}

export type IConfigurationRulePayerEditorProps =
    | IConfigurationRulePayerEditorRuleProps
    | IConfigurationRulePayerEditorListProps;

export const ConfigurationRulePayerEditor: React.FC<IConfigurationRulePayerEditorProps> = observer(props => {
    const { disabled, payerIds, store, showMatchHint = props.ruleId != null } = props;
    const isRuleMode = props.ruleId != null && props.matchIndex != null;
    const pickerKey = isRuleMode ? `${props.ruleId}|${props.matchIndex}|payer` : props.pickerKey;
    const isOpen = store.openPickerKey === pickerKey;
    const hasSelections = payerIds.length > 0;
    const selectedIds = useMemo(() => new Set(payerIds), [payerIds]);
    const pickerPanelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const delay = store.pickerQuery.trim() ? 250 : 0;
        const handle = window.setTimeout(() => {
            void store.searchPickerPayers(store.pickerQuery);
        }, delay);

        return () => window.clearTimeout(handle);
    }, [isOpen, store, store.pickerQuery]);

    useEffect(() => {
        if (!isOpen || !pickerPanelRef.current) {
            return;
        }

        pickerPanelRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [isOpen, store.pickerPayerOptions.length, store.pickerSearchLoading]);

    const options = isOpen
        ? store.pickerPayerOptions
              .filter(payer => !selectedIds.has(payer.payerId))
              .map(payer => ({
                  payer,
                  badge:
                      isRuleMode && props.ruleId != null && props.matchIndex != null
                          ? getUsageBadgePresentation(
                                store.getCatalogUsage(props.ruleId, props.matchIndex, 'payerId', payer.payerId),
                            )
                          : null,
              }))
        : [];

    const commitPayerIds = (nextIds: string[]): void => {
        if (props.onChange) {
            // Empty list configs (e.g. skipEligibilityForPayerList) must persist as [].
            props.onChange(nextIds);
        }
    };

    const handlePick = (payer: CatalogPayer): void => {
        if (isRuleMode && props.ruleId != null && props.matchIndex != null) {
            store.addPayer(props.ruleId, props.matchIndex, payer);
            return;
        }

        if (selectedIds.has(payer.payerId)) {
            return;
        }

        store.rememberPayers([payer]);
        commitPayerIds([...payerIds, payer.payerId]);
        store.setPickerState(pickerKey, '');
    };

    const handleRemove = (payerId: string): void => {
        if (isRuleMode && props.ruleId != null && props.matchIndex != null) {
            store.removePayer(props.ruleId, props.matchIndex, payerId);
            return;
        }

        commitPayerIds(payerIds.filter(id => id !== payerId));
    };

    const showPickerPanel = isOpen && !disabled;
    const showLoading = store.pickerSearchLoading;
    const showNoResults = !showLoading && options.length === 0 && store.pickerQuery.trim() !== '';

    return (
        <S.PayerEditor>
            {showMatchHint && (
                <S.PayerSectionHint>
                    <Icon path={mdiSubdirectoryArrowRight} size={0.75} color="#8e9293" aria-hidden={true} />
                    Matches any of these payers (OR)
                </S.PayerSectionHint>
            )}

            {payerIds.map((payerId, index) => {
                const payer = store.payerById[payerId];
                const usage =
                    isRuleMode && props.ruleId != null && props.matchIndex != null
                        ? getUsageBadgePresentation(
                              store.getCatalogUsage(props.ruleId, props.matchIndex, 'payerId', payerId),
                          )
                        : null;

                return (
                    <React.Fragment key={payerId}>
                        {showMatchHint && index > 0 && (
                            <S.PayerOrDivider>
                                <S.PayerOrLabel>OR</S.PayerOrLabel>
                                <S.PayerOrLine />
                            </S.PayerOrDivider>
                        )}

                        {payer ? (
                            <S.PayerCard>
                                <S.PayerCardIcon>
                                    <Icon path={mdiBankOutline} size={1.2} aria-hidden={true} />
                                </S.PayerCardIcon>
                                <S.PayerCardBody>
                                    <S.PayerCardName>{payer.name}</S.PayerCardName>
                                    <S.PayerCardMeta>{payer.meta}</S.PayerCardMeta>
                                </S.PayerCardBody>
                                {usage?.used && (
                                    <S.UsageBadge variant="used">
                                        <Icon path={mdiHistory} size={0.65} aria-hidden={true} />
                                        {usage.short}
                                    </S.UsageBadge>
                                )}
                                <S.PayerCardRemove
                                    type="button"
                                    disabled={disabled}
                                    aria-label={`Remove ${payer.name}`}
                                    onClick={() => handleRemove(payerId)}>
                                    <Icon path={mdiClose} size={0.7} aria-hidden={true} />
                                </S.PayerCardRemove>
                            </S.PayerCard>
                        ) : (
                            <S.PayerMissingCard>
                                <Icon path={mdiAlertCircle} size={0.85} color="#C47A17" aria-hidden={true} />
                                <S.PayerMissingText>
                                    Payer <S.PayerMissingId>{payerId}</S.PayerMissingId> could not be resolved.
                                </S.PayerMissingText>
                                <S.PayerCardRemove
                                    type="button"
                                    disabled={disabled}
                                    aria-label={`Remove unresolved payer ${payerId}`}
                                    onClick={() => handleRemove(payerId)}>
                                    <Icon path={mdiClose} size={0.7} aria-hidden={true} />
                                </S.PayerCardRemove>
                            </S.PayerMissingCard>
                        )}
                    </React.Fragment>
                );
            })}

            <S.PayerSearchWrap>
                <Icon path={mdiMagnify} size={0.6} color="#8e9293" aria-hidden={true} />
                <S.ChipInput
                    placeholder={
                        hasSelections
                            ? showMatchHint
                                ? 'Add another payer (OR)…'
                                : 'Add another payer…'
                            : 'Search payers by name…'
                    }
                    disabled={disabled}
                    readOnly={disabled}
                    value={isOpen ? store.pickerQuery : ''}
                    onFocus={() => {
                        if (!disabled) {
                            store.setPickerState(pickerKey, '');
                        }
                    }}
                    onChange={event => {
                        if (!disabled) {
                            store.setPickerState(pickerKey, event.target.value);
                        }
                    }}
                    onBlur={() => {
                        window.setTimeout(() => {
                            if (store.openPickerKey === pickerKey) {
                                store.setPickerState(null);
                            }
                        }, 170);
                    }}
                    onKeyDown={event => {
                        if (disabled) {
                            return;
                        }

                        if (event.key === 'Enter' && options[0]) {
                            event.preventDefault();
                            handlePick(options[0].payer);
                        }
                    }}
                />
            </S.PayerSearchWrap>

            {showPickerPanel && (
                <S.PayerPickerPanel ref={pickerPanelRef}>
                    <S.PayerPickerDropdown>
                        {showLoading ? (
                            <S.PayerPickerOption style={{ cursor: 'default' }}>
                                <S.PayerPickerName>Searching payers…</S.PayerPickerName>
                            </S.PayerPickerOption>
                        ) : options.length > 0 ? (
                            options.map(({ payer, badge }) => (
                                <S.PayerPickerOption
                                    key={payer.payerId}
                                    onMouseDown={event => {
                                        event.preventDefault();
                                        handlePick(payer);
                                    }}>
                                    <S.PayerPickerName>{payer.name}</S.PayerPickerName>
                                    <S.PayerPickerSub>{payer.meta}</S.PayerPickerSub>
                                    {badge ? (
                                        badge.used ? (
                                            <S.UsageBadge variant="used">
                                                <Icon path={mdiHistory} size={0.65} aria-hidden={true} />
                                                {badge.label}
                                            </S.UsageBadge>
                                        ) : (
                                            <S.UsageBadge variant="fresh">
                                                <Icon path={mdiCheck} size={0.65} aria-hidden={true} />
                                                {badge.label}
                                            </S.UsageBadge>
                                        )
                                    ) : null}
                                </S.PayerPickerOption>
                            ))
                        ) : showNoResults ? (
                            <S.PayerPickerOption style={{ cursor: 'default' }}>
                                <S.PayerPickerName>No payers found</S.PayerPickerName>
                            </S.PayerPickerOption>
                        ) : (
                            <S.PayerPickerOption style={{ cursor: 'default' }}>
                                <S.PayerPickerName>No payers available</S.PayerPickerName>
                            </S.PayerPickerOption>
                        )}
                    </S.PayerPickerDropdown>
                </S.PayerPickerPanel>
            )}
        </S.PayerEditor>
    );
});
